import { IMenuSection, MenuEventKeys } from '../models/headerMenu';
import { securityFunctions } from './securityFunctions';

// Event key constants for better maintainability
export const MENU_EVENT_KEYS: MenuEventKeys = {
    // Settings actions
    PROFILE: 'profile',
    CONTACT_US: 'contact-us',
    LOGOUT: 'logout',
    VIEW_AS_FACILITY: 'view-as-facility',
    VIEW_AS_ADMIN: 'view-as-admin',

    // Admin section
    BRANDS: 'brands',
    USERS: 'users',
    NEW_USER_QUEUE: 'new-user-queue',

    // Facility section
    ALERTS: 'alerts',
    FACILITY_INFORMATION: 'facility-information',
    FACILITY_LOCATIONS: 'facility-locations',
    FACILITY_QUEUE: 'facility-queue',

    // Jobs section
    JOBS: 'jobs',
    JOB_COMMENTS: 'job-comments',
    MEASUREMENTS: 'measurements',
    WORK_ORDERS: 'work-orders',

    // Sales section
    LEADS: 'leads',
    PRODUCTS: 'products',
    NEW_PRODUCTS_QUEUE: 'new-products-queue',
    SUGGESTED_PARTS: 'suggested-parts',

    // Reports section
    REPORT_BUILDER: 'report-builder',
    REPORT_PRINTING: 'report-printing',
    STANDARD_TEXT_LIBRARY: 'standard-text-library'
} as const;

// Internal email domains configuration
export const INTERNAL_EMAIL_DOMAINS = [
    '@beaconmedaes.com',
    '@atlascopco.com',
    '@thebigpixel.net',
    '@class1.com'
] as const;

// Menu configuration
export const MENU_SECTIONS: IMenuSection[] = [
    {
        title: 'ADMIN',
        items: [
            {
                eventKey: MENU_EVENT_KEYS.BRANDS,
                label: 'Brands',
                description: 'Manage equipment brands and manufacturers',
                url: '/brands',
                securityFunctions: [securityFunctions.ManageBrands.id]
            },
            {
                eventKey: MENU_EVENT_KEYS.USERS,
                label: 'Users',
                description: 'Manage user accounts and permissions',
                url: '/users',
                securityFunctions: [securityFunctions.ManageAllUsers.id]
            },
            {
                eventKey: MENU_EVENT_KEYS.NEW_USER_QUEUE,
                label: 'New User Queue',
                description: 'Review and approve pending user registrations',
                url: '/queue',
                securityFunctions: [securityFunctions.ManageAllUsers.id]
            }
        ]
    },
    {
        title: 'FACILITY',
        items: [
            {
                eventKey: MENU_EVENT_KEYS.ALERTS,
                label: 'Alerts',
                description: 'Monitor equipment alerts',
                url: '/alerts',
                securityFunctions: [securityFunctions.ManageAlerts.id]
            },
            {
                eventKey: MENU_EVENT_KEYS.FACILITY_INFORMATION,
                label: 'Facility Information',
                description: 'View and manage facility details',
                url: '/facility',
                securityFunctions: [
                    securityFunctions.ManageFacilities.id,
                    securityFunctions.ViewCustomers.id
                ]
            },
            {
                eventKey: MENU_EVENT_KEYS.FACILITY_LOCATIONS,
                label: 'Facility Locations',
                description: 'Manage facility rooms and locations',
                url: '/locations',
                securityFunctions: [securityFunctions.ManageLocations.id]
            },
            {
                eventKey: MENU_EVENT_KEYS.FACILITY_QUEUE,
                label: 'Facility Queue',
                description: 'Review pending facility updates',
                url: '/facilityqueue',
                securityFunctions: [securityFunctions.ManageFacilities.id]
            }
        ]
    },
    {
        title: 'JOBS',
        items: [
            {
                eventKey: MENU_EVENT_KEYS.JOBS,
                label: 'Jobs',
                description: 'Manage service jobs and assignments',
                url: '/managejobs',
                securityFunctions: [securityFunctions.ManageJobs.id]
            },
            {
                eventKey: MENU_EVENT_KEYS.JOB_COMMENTS,
                label: 'Job Comments',
                description: 'View and manage job-related comments',
                url: '/job_comments',
                securityFunctions: [securityFunctions.ManageJobComments.id]
            },
            {
                eventKey: MENU_EVENT_KEYS.MEASUREMENTS,
                label: 'Measurements',
                description: 'Configure measurement point lists',
                url: '/measurements',
                securityFunctions: [
                    securityFunctions.ManageAllMeasurementPoints.id
                ]
            },
            {
                eventKey: MENU_EVENT_KEYS.WORK_ORDERS,
                label: 'Work Orders',
                description: 'Manage customer work orders',
                url: '/workorders',
                securityFunctions: [
                    securityFunctions.ManageCustomerWorkOrders.id
                ]
            }
        ]
    },
    {
        title: 'SALES',
        items: [
            {
                eventKey: MENU_EVENT_KEYS.LEADS,
                label: 'Leads',
                description: 'Track and manage sales opportunities',
                url: '/leads',
                securityFunctions: [
                    securityFunctions.SalesAdmin.id,
                    securityFunctions.SalesInstallation.id,
                    securityFunctions.SalesManager.id,
                    securityFunctions.SalesParts.id,
                    securityFunctions.SalesService.id
                ]
            },
            {
                eventKey: MENU_EVENT_KEYS.PRODUCTS,
                label: 'Products',
                description: 'Manage product catalog',
                url: '/products',
                securityFunctions: [securityFunctions.ManageProducts.id]
            },
            {
                eventKey: MENU_EVENT_KEYS.NEW_PRODUCTS_QUEUE,
                label: 'New Products Queue',
                description: 'Review pending product submissions',
                url: '/productqueue',
                securityFunctions: [securityFunctions.ManageProducts.id]
            },
            {
                eventKey: MENU_EVENT_KEYS.SUGGESTED_PARTS,
                label: 'Suggested Parts',
                description: 'Manage recommended replacement parts',
                url: '/suggested_parts',
                securityFunctions: [
                    securityFunctions.ManageSuggestedParts.id,
                    securityFunctions.ViewSuggestedParts.id
                ]
            }
        ]
    },
    {
        title: 'REPORTS',
        items: [
            {
                eventKey: MENU_EVENT_KEYS.REPORT_BUILDER,
                label: 'Report Builder',
                description: 'Create custom reports',
                url: '/reportBuilder',
                securityFunctions: [securityFunctions.ManageReports.id]
            },
            {
                eventKey: MENU_EVENT_KEYS.REPORT_PRINTING,
                label: 'Report Printing',
                description: 'Generate and print reports',
                url: '/reports',
                securityFunctions: [securityFunctions.RunReports.id]
            },
            {
                eventKey: MENU_EVENT_KEYS.STANDARD_TEXT_LIBRARY,
                label: 'Standard Text Library',
                description: 'Manage reusable report text templates',
                url: '/codes',
                securityFunctions: [securityFunctions.ManageCodes.id]
            }
        ]
    },
    {
        title: 'SETTINGS',
        items: [
            {
                eventKey: MENU_EVENT_KEYS.VIEW_AS_FACILITY,
                label: 'View as Facility',
                description: 'Show all menu items for a facility role',
                icon: 'building'
            },
            {
                eventKey: MENU_EVENT_KEYS.PROFILE,
                label: 'Profile',
                description:
                    'Set preferences standards, filter, language, and updates',
                icon: 'user'
            },
            {
                eventKey: MENU_EVENT_KEYS.LOGOUT,
                label: 'Logout',
                description: 'Logout from the system',
                icon: ['far', 'sign-out']
            }
        ]
    }
];
